import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  MenuProps,
  Select as MUISelect,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material'
import React, { forwardRef } from 'react'

export interface OptionProps {
  value: string
  label: string
  disabled?: boolean
  icon?: React.ReactNode
}

export interface GroupedOption {
  label: string
  options: OptionProps[]
}

export interface SelectProps {
  className?: string
  disabled?: boolean
  label?: string
  icon?: React.ReactNode
  name: string
  required?: boolean
  options: Array<OptionProps | GroupedOption>
  onChange?: (
    event: React.SyntheticEvent | null,
    value: string,
    name: string,
  ) => void
  placeholder?: string
  value?: string
  onClick?: (event: React.MouseEvent, name?: string) => void
  error?: string
  /** @deprecated Use MenuProps instead */
  slotProps?: {
    popper?: { style?: React.CSSProperties }
  }
  MenuProps?: Partial<MenuProps>
  sx?: SxProps<Theme>
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      onClick,
      className,
      disabled,
      icon,
      label,
      name,
      onChange,
      options,
      required,
      placeholder,
      value,
      error,
      slotProps,
      MenuProps: menuPropsProp,
      sx,
    },
    ref,
  ) => {
    const handleOnChange = (event: SelectChangeEvent<string>) => {
      // Cast to SyntheticEvent for backward compatibility with old onChange signature
      onChange?.(
        event as unknown as React.SyntheticEvent,
        event.target.value,
        name,
      )
    }

    // Convert deprecated slotProps.popper.style.zIndex to MenuProps
    const popperZIndex = slotProps?.popper?.style?.zIndex
    const mergedMenuProps: Partial<MenuProps> | undefined =
      popperZIndex || menuPropsProp
        ? {
            ...menuPropsProp,
            sx: {
              ...(menuPropsProp?.sx as object),
              ...(popperZIndex && { zIndex: popperZIndex }),
            },
          }
        : undefined

    const renderMenuItems = () => {
      const items: React.ReactNode[] = []

      options.forEach((optionOrGroup, index) => {
        if ('options' in optionOrGroup) {
          items.push(
            <ListSubheader key={`group-${index}`}>
              {optionOrGroup.label}
            </ListSubheader>,
          )
          optionOrGroup.options.forEach((opt) => {
            items.push(
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                data-cy={`select-option-${opt.value}`}
              >
                {opt.icon && (
                  <Box sx={{ mr: 1, display: 'flex' }}>{opt.icon}</Box>
                )}
                {opt.label}
              </MenuItem>,
            )
          })
        } else {
          items.push(
            <MenuItem
              key={optionOrGroup.value}
              value={optionOrGroup.value}
              disabled={optionOrGroup.disabled}
              data-cy={`select-option-${optionOrGroup.value}`}
            >
              {optionOrGroup.icon && (
                <Box sx={{ mr: 1, display: 'flex' }}>{optionOrGroup.icon}</Box>
              )}
              {optionOrGroup.label}
            </MenuItem>,
          )
        }
      })

      return items
    }

    return (
      <FormControl
        ref={ref}
        className={className}
        disabled={disabled}
        required={required}
        error={Boolean(error)}
        fullWidth
        sx={sx}
      >
        {label && <InputLabel id={`${name}-label`}>{label}</InputLabel>}
        <MUISelect
          id={name}
          labelId={label ? `${name}-label` : undefined}
          value={value ?? ''}
          label={label}
          onChange={handleOnChange}
          onClick={(e: React.MouseEvent<Element>) => onClick?.(e, name)}
          displayEmpty={Boolean(placeholder)}
          startAdornment={icon}
          MenuProps={mergedMenuProps}
          renderValue={(selected) => {
            if (!selected && placeholder) {
              return <Box sx={{ color: 'text.secondary' }}>{placeholder}</Box>
            }
            const allOptions = options.flatMap((o) =>
              'options' in o ? o.options : [o],
            )
            const selectedOption = allOptions.find((o) => o.value === selected)
            return selectedOption?.label ?? selected
          }}
        >
          {renderMenuItems()}
        </MUISelect>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    )
  },
)

Select.displayName = 'Select'

export default Select
